import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { MessageInstance } from 'antd/es/message/interface';
import {
  RecordingResponse,
  updateSummaryResponseSchema,
  UpdateRecordingBody,
  updateRecordingResponseSchema,
  retryRecordingResponseSchema,
} from '@minutes-system/backend/src/api/recordings/schema';
import { Advisor } from '@minutes-system/backend/src/api/advisors/schema';
import { Customer } from '@minutes-system/backend/src/api/customers/schema';
import { recordingQueryAtom } from '../states/state';
import { recordingsAtom } from '../../list/states/state';
import { advisorsQueryAtom, buildAdvisorsQueryKey } from '../../../../states/advisors';
import { customersQueryAtom, buildQueryKey } from '../../../../states/customers';
import { ProblemDetailsSchema } from '@minutes-system/backend/src/http/problemDetails';
import { guardedClient } from '../../../auth/api/clients';
import fetchAndValidate from '../../../../lib/fetchAndValidate';
import { HttpError } from '../../../../shared/httpError';

interface UseRecordingDetailReturn {
  recording: RecordingResponse;
  advisors: Advisor[];
  customers: Customer[];
  updateSummary: (summaryText: string) => Promise<{ success: boolean }>;
  updateRecording: (data: UpdateRecordingBody) => Promise<{ success: boolean }>;
  retryRecording: () => Promise<{ success: boolean }>;
}

const useRecordingDetail = (id: number, messageApi: MessageInstance): UseRecordingDetailReturn => {
  const [recording, refreshRecording] = useAtom(recordingQueryAtom(id));
  const { advisors } = useAtomValue(advisorsQueryAtom(buildAdvisorsQueryKey({})));
  const { customers } = useAtomValue(customersQueryAtom(buildQueryKey({})));
  const refreshRecordings = useSetAtom(recordingsAtom);

  const updateSummary = useCallback(
    async (summaryText: string): Promise<{ success: boolean }> => {
      try {
        const response = await guardedClient.recordings[':id'].summary.$patch({
          param: { id: String(id) },
          json: { summaryText },
        });
        await fetchAndValidate(Promise.resolve(response), updateSummaryResponseSchema);
        refreshRecording();
        return { success: true };
      } catch (error) {
        console.error('Failed to update summary:', error);
        messageApi.error('要約の更新に失敗しました');
        return { success: false };
      }
    },
    [id, messageApi, refreshRecording]
  );

  const updateRecording = useCallback(
    async (data: UpdateRecordingBody): Promise<{ success: boolean }> => {
      try {
        const response = await guardedClient.recordings[':id'].$put({
          param: { id: String(id) },
          json: data,
        });
        await fetchAndValidate(Promise.resolve(response), updateRecordingResponseSchema);
        refreshRecording();
        refreshRecordings();
        return { success: true };
      } catch (error) {
        console.error('Failed to update recording:', error);
        messageApi.error('録音情報の更新に失敗しました');
        return { success: false };
      }
    },
    [id, messageApi, refreshRecording, refreshRecordings]
  );

  const retryRecording = useCallback(async (): Promise<{ success: boolean }> => {
    try {
      const response = await guardedClient.recordings[':id'].retry.$post({
        param: { id: String(id) },
      });
      await fetchAndValidate(Promise.resolve(response), retryRecordingResponseSchema);
      refreshRecording();
      return { success: true };
    } catch (error) {
      console.error('Failed to retry recording:', error);
      if (error instanceof HttpError && error.status === 400) {
        const problem = ProblemDetailsSchema.safeParse(error.body);
        messageApi.error(
          problem.success ? (problem.data.detail ?? 'リトライに失敗しました') : 'リトライに失敗しました'
        );
      } else if (error instanceof HttpError && error.status === 409) {
        messageApi.error('既にリトライが開始されています');
      } else {
        messageApi.error('リトライに失敗しました');
      }
      return { success: false };
    }
  }, [id, messageApi, refreshRecording]);

  return { recording, advisors, customers, updateSummary, updateRecording, retryRecording };
};

export default useRecordingDetail;
